import { useMemo } from 'react';

import type { Segmentation } from '@core';
import { hexColors } from '@ui';

import { getKeypointId } from '../helpers';

import { MultiColorTracer, type SplitPointConfigItem, type TrackData } from './Tracer';

import type { FrameFirstSkeleton } from '../skeletonUtils';
import type { KeypointIdToName } from '../types';

type HandPathProps = {
    frame?: number;
    skeleton: FrameFirstSkeleton;
    keypointIdToName: KeypointIdToName;
    segmentation: Segmentation;
    opacity?: number;
};

export function HandPath({ frame = 0, skeleton, keypointIdToName, segmentation, opacity = 1 }: HandPathProps) {
    const pointId = getKeypointId('right_index_base', keypointIdToName);

    const derivedTrack: TrackData = useMemo(() => {
        const points: TrackData = [];
        const maxFrameToProcess = Math.min(frame, skeleton.length - 1);

        for (let i = 0; i <= maxFrameToProcess; i++) {
            const frameData = skeleton[i];
            const keypointData = frameData?.[pointId];

            if (Array.isArray(keypointData) && keypointData.length >= 3) {
                points.push([keypointData[0], keypointData[1], keypointData[2]]);
            } else {
                points.push(null);
            }
        }
        return points;
    }, [frame, skeleton, pointId]);

    const handPathSplitConfig: SplitPointConfigItem[] = useMemo(
        () => [
            {
                min: segmentation['P1'],
                max: segmentation['P4'],
                color: hexColors.blue,
                lineWidth: 0.01,
            },
            {
                min: segmentation['P4'] + 1,
                max: segmentation['P7'] - 1,
                color: hexColors.orange,
                lineWidth: 0.01,
            },
            {
                min: segmentation['P7'],
                max: segmentation['P7'] + 1,
                color: hexColors.black,
                lineWidth: 0.015,
            },
            {
                min: segmentation['P7'] + 2,
                max: segmentation['P10'],
                color: hexColors.magenta,
                lineWidth: 0.01,
            },
        ],
        [segmentation],
    );

    return (
        <MultiColorTracer frame={frame} track={derivedTrack} splitPointConfig={handPathSplitConfig} opacity={opacity} />
    );
}
